<template>
  <div class="toggle__buttons">
    <div class="toggle-button" v-if="(props.items && props.items.length) || !$slots.empty">
      <div
        class="toggle-button__item"
        v-for="(row, index) in displayedItems"
        :key="row.id"
        @click="select(row)"
        :class="{ 'toggle-button__item--active': isSelected(row), 'toggle-button__item--error': hasError(index) }"
      >
        <span class="toggle-button__item-content">
          <span v-if="!$slots.item">{{ row[props.textField] }}</span>
          <slot name="item" :value="row"/>
        </span>
      </div>
      <s-popper
        v-if="props.maxItems"
        placement="bottom"
        tag="li"
        class="nav-item d-flex mt-0"
      >
        <div class="navbar-nav-link dropdown-toggle p-0">
          <div
            class="d-flex align-items-center py-1 px-1 navbar-nav-link--hover"
            style="height: 28px;"
          >
            <div class="toggle-button__item-content toggle-more-button">...</div>
          </div>
        </div>
        <template #popup>
          <div class="dropdown-menu toggle-menu">
            <div
              @click="select(row, true)"
              :class="{ 'toggle-button__item--error': hasError(index) }"
              v-for="(row, index) in hiddenItems"
              :key="row.id"
            >
              <span class="toggle-button__item-content dropdown-item toggle-drop-item">
                <span v-if="!$slots.dropdown">{{ row[props.keyField] }}</span>
                <slot name="dropdown" :value="row"/>
              </span>
            </div>
          </div>
        </template>
      </s-popper>
    </div>
    <slot name="empty" v-else-if="$slots.empty"/>
  </div>
</template>


<script setup>
  import { computed, onMounted, toRef } from 'vue';

  const emit = defineEmits(['openModal', 'update:modelValue', 'update:sortedItems']);

  const props = defineProps({
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    modelValue: {
      type: null,
      required: true,
    },
    textField: {
      type: String,
      default: 'title',
    },
    keyField: {
      type: String,
      default: 'id',
    },
    maxItems: {
      type: null,
      default: null,
    },
    nullable: {
      type: Boolean,
      default: false,
    },
    modalTitle: {
      type: String,
      required: false,
      default: null,
    },
    emptyItem: {
      type: null,
      default: null,
    },
    selectOtherText: {
      type: String,
      default: '+',
    },
    matchBy: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorIndexes: {
      type: Array,
      default: () => [],
    },
  });
  const sortedItems = toRef(props, 'items');

  const filteredItems = computed(() => {
    if (sortedItems.value) {
      return sortedItems.value.filter(el => el !== undefined);
    }

    return [];
  });

  const displayedItems = computed(() => props.maxItems && filteredItems.value.length ? filteredItems.value.slice(0, props.maxItems) : filteredItems.value);

  const hiddenItems = computed(() => props.maxItems && filteredItems.value.length > props.maxItems ?
    filteredItems.value.slice(props.maxItems) :
    []);

  const modelKey = computed(() => props.matchBy ? props.modelValue[props.matchBy] : props.modelValue);

  onMounted(() => {
    sortedItems.value.values = props.items;
  });

  function isSelected(row) {
    if (!props.modelValue || !row) {
      return false;
    }

    return row[props.keyField] === modelKey.value;
  }

  function select(row, unshift = false) {
    if (props.disabled) {
      return;
    }

    if (isSelected(row)) {
      if (props.nullable) {
        emit('update:modelValue', null);
      }
      return;
    }

    if (props.matchBy) {
      emit('update:modelValue', row);
    } else {
      emit('update:modelValue', row[props.keyField]);
    }

    if (unshift) {
      const hiddenIndex = hiddenItems.value.findIndex(it => it[props.keyField] === row[props.keyField]);
      if (hiddenIndex !== -1) {
        hiddenItems.value.splice(hiddenIndex, 1);
      }
      displayedItems.value.unshift(row);
      if (props.maxItems && displayedItems.value.length > props.maxItems) {
        const lastItem = displayedItems.value.pop();
        hiddenItems.value.push(lastItem);
      }

      emit('update:sortedItems', [...displayedItems.value, ...hiddenItems.value]);
    }
  }

  function hasError(index) {
    return props.errorIndexes.includes(index);
  }
</script>
